/*
 * Rate adequacy arithmetic for the Rate Adequacy tab.
 *
 * Every input and output is a decimal (0.5 is 50%). Any missing or non-finite
 * input makes the outputs that depend on it null, never NaN, so the UI can show
 * a blank cell instead of "NaN%".
 *
 * Formulas (the Client's 2 Sep 2026 note and 16 Sep 2026 hand-drawn table):
 *   combined ratio        = loss pick + expected commission + brokerage + expenses
 *   target combined ratio = target loss ratio + expected commission + brokerage + expenses
 *   adequacy              = target combined ratio / combined ratio
 *   rate change           = current adequacy / prior adequacy - 1
 *
 * If the Client defines any of these differently, each is a one-line change here.
 */

const isNumber = (value) => typeof value === "number" && Number.isFinite(value);

const clean = (value) => (isNumber(value) ? value : null);

const sum = (...values) =>
  values.every(isNumber)
    ? values.reduce((total, value) => total + value, 0)
    : null;

const ratio = (numerator, denominator) =>
  isNumber(numerator) && isNumber(denominator) && denominator !== 0
    ? numerator / denominator
    : null;

export const computeRateAdequacy = ({
  lossPick,
  expectedCommission,
  brokerage,
  expenses,
  targetLossRatio,
} = {}) => {
  const combinedRatio = sum(lossPick, expectedCommission, brokerage, expenses);
  const targetCombinedRatio = sum(
    targetLossRatio,
    expectedCommission,
    brokerage,
    expenses
  );
  return {
    lossPick: clean(lossPick),
    expectedCommission: clean(expectedCommission),
    brokerage: clean(brokerage),
    expenses: clean(expenses),
    targetLossRatio: clean(targetLossRatio),
    combinedRatio,
    targetCombinedRatio,
    adequacy: ratio(targetCombinedRatio, combinedRatio),
  };
};

export const computeRateChange = (currentAdequacy, priorAdequacy) => {
  const change = ratio(currentAdequacy, priorAdequacy);
  return change == null ? null : change - 1;
};
